import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';

const Container = styled.div`
  background-color: #f7f7f7;
  min-height: 100vh;
`;

const Header = styled.header`
  background-color: white;
  padding: 20px;
  text-align: center;
  border-bottom: 1px solid #eee;
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 1.5em;
  cursor: pointer;

  &:hover {
    transform: translateY(-50%);
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5em;
  font-family: ${theme.typography.headingFont || 'Poppins, sans-serif'};
  color: ${theme.colors.deepNavy};
`;

const Content = styled.main`
  padding: 20px;
  max-width: 700px;
  margin: 0 auto;
`;

const UpdatedDate = styled.p`
  font-size: 12px;
  color: #808080;
  text-align: center;
  margin: 0 0 24px;
`;

const Section = styled.section`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.headingFont || 'Poppins, sans-serif'};
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.deepNavy};
  margin: 0 0 12px;
`;

const SectionText = styled.p`
  font-family: ${theme.typography.bodyFont || 'Inter, sans-serif'};
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  margin: 0 0 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionList = styled.ul`
  font-family: ${theme.typography.bodyFont || 'Inter, sans-serif'};
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  margin: 0 0 12px;
  padding-left: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ListItem = styled.li`
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EmailLink = styled.a`
  color: #007bff;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const PrivacyPolicyScreen = () => {
  const navigate = useNavigate();

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(-1)} aria-label="Go back">
          &larr;
        </BackButton>
        <Title>Privacy Policy</Title>
      </Header>
      <Content>
        <UpdatedDate>Last updated: February 2026</UpdatedDate>

        <Section>
          <SectionTitle>1. Introduction</SectionTitle>
          <SectionText>
            Light & Cover ("we," "us," or "our") operates the Lightway application ("Service"). This Privacy Policy describes what information we collect, how we use it, and what choices you have regarding your data. By using the Service, you agree to the collection and use of information as described in this policy.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>2. Information We Collect</SectionTitle>
          <SectionText>
            We collect the following types of information when you use the Service:
          </SectionText>
          <SectionText>
            <strong>Account Information:</strong> Your name and email address, provided during account creation.
          </SectionText>
          <SectionText>
            <strong>Advocacy Activities:</strong> Activity titles, descriptions, reflections, photos, timestamps, cause selections, and level information that you choose to log.
          </SectionText>
          <SectionText>
            <strong>Usage Data:</strong> Feature usage patterns, sharing activity, goal settings, and template usage to help us understand how the Service is used.
          </SectionText>
          <SectionText>
            <strong>Device Information:</strong> Browser type and operating system, collected when you submit feedback to help us diagnose issues.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>3. How We Use Your Information</SectionTitle>
          <SectionText>
            We use the information we collect to:
          </SectionText>
          <SectionList>
            <ListItem>Provide, maintain, and improve the Service</ListItem>
            <ListItem>Store and display your advocacy activities as you request</ListItem>
            <ListItem>Generate share cards and export reports on your behalf</ListItem>
            <ListItem>Track milestones and goals based on your activity</ListItem>
            <ListItem>Respond to your feedback and support requests</ListItem>
            <ListItem>Improve the Service based on usage patterns and feedback</ListItem>
          </SectionList>
        </Section>

        <Section>
          <SectionTitle>4. Data Storage and Security</SectionTitle>
          <SectionText>
            Your data is stored using Google Firebase services, including Cloud Firestore for structured data and Firebase Storage for photos and images. All data is encrypted in transit using HTTPS and encrypted at rest using Firebase's default encryption.
          </SectionText>
          <SectionText>
            Access to your data is restricted to your authenticated account. Our security rules ensure that users can only read and write their own data. While we take reasonable measures to protect your information, no method of electronic storage is 100% secure.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>5. Data Sharing</SectionTitle>
          <SectionText>
            We do not sell your personal information to third parties. We do not share your data with third parties except in the following circumstances:
          </SectionText>
          <SectionList>
            <ListItem>When required by law or in response to a valid legal process</ListItem>
            <ListItem>To protect the rights, property, or safety of Light & Cover, our users, or the public</ListItem>
            <ListItem>With service providers who help us operate the Service (such as Google Firebase), subject to their own privacy policies</ListItem>
          </SectionList>
          <SectionText>
            Share cards are generated locally on your device and shared manually by you. We do not automatically post or share your content with any external platforms.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>6. Your Rights</SectionTitle>
          <SectionText>
            You have the following rights regarding your data:
          </SectionText>
          <SectionList>
            <ListItem><strong>Export:</strong> Download all your data at any time in JSON, PDF, and CSV formats from Privacy Settings</ListItem>
            <ListItem><strong>Delete:</strong> Delete your account through Privacy Settings, with a 21-day grace period to change your mind</ListItem>
            <ListItem><strong>Control sharing:</strong> Choose whether your name appears on share cards via the privacy toggle</ListItem>
            <ListItem><strong>Communication preferences:</strong> Opt in or out of email updates at any time from Notification Preferences</ListItem>
          </SectionList>
        </Section>

        <Section>
          <SectionTitle>7. Data Retention</SectionTitle>
          <SectionText>
            <strong>Active accounts:</strong> Your data is retained for as long as your account remains active.
          </SectionText>
          <SectionText>
            <strong>Deleted accounts:</strong> When you request account deletion, your data is retained for a 21-day grace period. During this time, you can restore your account by logging back in. After the grace period, all your data is permanently and irreversibly deleted, including activities, reflections, photos, templates, goals, and account information.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>8. Children's Privacy</SectionTitle>
          <SectionText>
            Lightway is available to users ages 13 and older. We do not knowingly collect personal information from children under the age of 13. If we become aware that a child under 13 has provided us with personal information, we will take steps to delete that information promptly.
          </SectionText>
          <SectionText>
            Users between the ages of 13 and 18 should have the consent of a parent or legal guardian before using the Service.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>9. Changes to This Policy</SectionTitle>
          <SectionText>
            We may update this Privacy Policy from time to time. When we make changes, we will update the "Last updated" date at the top of this page. We will notify users of significant changes through the Service. Your continued use of the Service after any changes constitutes your acceptance of the updated policy.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>10. Contact</SectionTitle>
          <SectionText>
            If you have any questions about this Privacy Policy or how your data is handled, please contact us at{' '}
            <EmailLink href="mailto:dev@lightandcover.org">dev@lightandcover.org</EmailLink>.
          </SectionText>
        </Section>
      </Content>
    </Container>
  );
};
